import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Circle,
  FileText,
  Mail,
  MapPin,
  Phone,
  UserRound,
  WalletCards,
} from "lucide-react";
import { projectApi } from "../../backend/client";
import { taskLabel, useProjectTasks } from "../../hooks/useProjectTasks";
import {
  ProjectStatusBadge,
  ProjectTypeBadge,
} from "../../components/domain-badges";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

const ClientProjectDetails = ({ mode }: { mode?: "agent-peek" }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAgentPeek =
    mode === "agent-peek" || location.state?.fromAgent === true;
  const [project, setProject] = useState(null);
  const {
    visibleTasks,
    totalTasks,
    percentageComplete,
    currentStep,
    currentTaskLabel,
  } = useProjectTasks(projectId);

  const fetchProjectDetails = useCallback(async () => {
    const { data, error } = await projectApi.get(projectId);

    if (error) {
      console.error("Error fetching project details:", error);
      return;
    }
    setProject(data);
  }, [projectId]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  const handleBack = () => {
    if (isAgentPeek) navigate("/projects");
    else navigate("/my-projects");
  };

  return (
    <div className="flex flex-col">
      <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-5xl flex-col gap-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">Project details</h1>
              <p className="text-sm text-muted-foreground">
                Relocation summary, progress, task checklist, and quick links
                to documents and budgeting.
              </p>
            </div>
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>

          {!project ? (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                Loading project details...
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader className="p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle>
                        {project.relocating_from || "Origin"} to{" "}
                        {project.relocating_to || "Destination"}
                      </CardTitle>
                      <CardDescription>Project #{project.id}</CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ProjectTypeBadge type={project.project_type} />
                      <ProjectStatusBadge status={project.status} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 p-4 pt-0 sm:grid-cols-2 sm:p-5 sm:pt-0">
                  <InfoField
                    icon={CalendarDays}
                    label="Start date"
                    value={formatDate(project.project_start_date)}
                  />
                  <InfoField
                    icon={CalendarDays}
                    label="End date"
                    value={formatDate(project.project_end_date)}
                  />
                  <InfoField
                    icon={MapPin}
                    label="Relocating from"
                    value={project.relocating_from || "N/A"}
                  />
                  <InfoField
                    icon={MapPin}
                    label="Relocating to"
                    value={project.relocating_to || "N/A"}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle>Project progress</CardTitle>
                      <CardDescription>
                        Step {currentStep} of {totalTasks || 1}
                      </CardDescription>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {percentageComplete}%
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                  <div className="h-3 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${percentageComplete}%` }}
                    />
                  </div>
                  <p className="mt-4 text-sm">
                    <span className="text-muted-foreground">
                      Current task:{" "}
                    </span>
                    <span className="font-medium">{currentTaskLabel}</span>
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 sm:p-5">
                  <CardTitle>Task checklist</CardTitle>
                  <CardDescription>
                    Tasks visible to you for this project, in order.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                  {visibleTasks.length ? (
                    <ol className="space-y-2 text-sm">
                      {visibleTasks.map((t, index) => (
                        <li
                          key={t.id ?? index}
                          className="flex items-center gap-2 p-2"
                        >
                          {t.is_complete ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-tone-success-foreground" />
                          ) : (
                            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span>{taskLabel(t)}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No tasks to show yet.
                    </p>
                  )}
                </CardContent>
              </Card>

              <div className="grid gap-5 sm:grid-cols-2">
                <Card>
                  <CardHeader className="p-4 sm:p-5">
                    <CardTitle>Documents</CardTitle>
                    <CardDescription>
                      View and download relocation documents
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                    <Button
                      onClick={() =>
                        navigate("/documents", { state: { projectId } })
                      }
                    >
                      <FileText className="h-4 w-4" />
                      Open documents
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="p-4 sm:p-5">
                    <CardTitle>Budgeting</CardTitle>
                    <CardDescription>
                      Review budget, paid and outstanding items
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                    <Button
                      onClick={() =>
                        navigate("/budgeting", {
                          state: isAgentPeek
                            ? { projectId, fromAgent: true }
                            : { projectId },
                        })
                      }
                    >
                      <WalletCards className="h-4 w-4" />
                      Open budgeting
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <Card>
                  <CardHeader className="p-4 sm:p-5">
                    <CardTitle>Customer</CardTitle>
                    <CardDescription>Client contact details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4 pt-0 sm:p-5 sm:pt-0">
                    <InfoField
                      icon={UserRound}
                      label="Name"
                      value={
                        project.customer
                          ? `${project.customer.firstname} ${project.customer.lastname}`
                          : "N/A"
                      }
                    />
                    <InfoField
                      icon={Mail}
                      label="Email"
                      value={project.customer?.email || "N/A"}
                    />
                    <InfoField
                      icon={Phone}
                      label="Contact number"
                      value={project.customer?.contactnumber || "N/A"}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="p-4 sm:p-5">
                    <CardTitle>Assigned agent</CardTitle>
                    <CardDescription>
                      Primary relocation contact
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4 pt-0 sm:p-5 sm:pt-0">
                    <InfoField
                      icon={UserRound}
                      label="Name"
                      value={
                        project.agent
                          ? `${project.agent.firstname} ${project.agent.lastname}`
                          : "N/A"
                      }
                    />
                    <InfoField
                      icon={Mail}
                      label="Email"
                      value={project.agent?.email || "N/A"}
                    />
                    <InfoField
                      icon={Phone}
                      label="Contact number"
                      value={project.agent?.contactnumber || "N/A"}
                    />
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border bg-card p-3">
    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <div className="mt-1 break-words text-sm font-medium">{value}</div>
  </div>
);

const formatDate = (dateString) =>
  dateString ? new Date(dateString).toLocaleDateString() : "N/A";

export default ClientProjectDetails;
